
<template>
  <div class="multiple-choice-component-container">
    <template v-for="answer in answers">
      <input
        :id="`answer-${answer.id}`"
        :key="`input-${answer.id}`"
        v-model="selected"
        :disabled="answered"
        :value="answer.id"
        type="checkbox"
      >
      <label
        :key="`label-${answer.id}`"
        :for="`answer-${answer.id}`"
        :class="{ selected: selected.includes(answer.id), answered: answered, strike: strikes.includes(answer.id) && !answered, correct: answered && answer.correct, box: true }"
        :tabindex="answered ? -1 : 0"
        aria-role="checkbox"
        :aria-checked="selected.includes(answer.id)"
        :aria-disabled="answered"
        @keydown.space.self="selectAnswer(answer.id)"
        @keydown.enter.self="selectAnswer(answer.id)"
      >
        <div :class="[{ stats: showMetaData }, 'fill']">
          <span>{{ answerPercentage(answer.id) }}%</span>
        </div>
        <div class="text">
          <div
            class="answer-text"
            :content="answer.content"
            v-html="answer.content"
          />
          <div
            v-if="answered && answer.rationale"
            :class="['rationale-text', {locked: lockedRationales}]"
            v-html="answer.rationale"
          />
        </div>
        <button
          v-if="!answered"
          class="strike-button"
          @click="strike(answer.id, $event)"
        >
          <icon
            class="white-icon"
            name="cross-thin"
            variant="white"
          />
          <icon
            class="colored-icon"
            name="cross-thin"
          />
        </button>
      </label>
    </template>
  </div>
</template>

<script>
import Icon from '@/components/singletons/icon';

export default {
  components: { Icon },
  props: {
    answered: {
      type: Boolean,
      default: false,
    },
    answerIds: {
      type: Array,
    },
    flashcard: {
      type: Object,
      default: () => ({}),
    },
    flashcardState: {
      type: Object,
      default: () => ({}),
    },
    lockedRationales: {
      type: Boolean,
      default: false,
    },
    metadata: {
      type: Object,
    },
  },
  data() {
    return {
      strikes: [],
    };
  },
  computed: {
    selected: {
      get() {
        if (this.flashcardState.answerIds) {
          return this.flashcardState.answerIds;
        }
        return this.answerIds;
      },
      set(value) {
        const changed = value[value.length - 1];
        this.$emit('set-answer-ids', value);
        let index = this.strikes.findIndex(a => a == changed);
        if(index >= 0) {
          this.strikes.splice(index, 1);
        }
      },
    },
    answers() {
      let answers = this.flashcard.answers || [];
      return answers.sort((a, b) => a.sortOrder - b.sortOrder);
    },
    showMetaData() {
      return this.answered && this.metadata?.isValid;
    },
  },
  methods: {
    answerPercentage(answerId) {
      const answer = this.metadata?.answers?.find(a => a.answerId == answerId);
      return answer?.percentage || 0;
    },
    selectAnswer(answerId) {
      if (this.answered) {
        // An added check to make sure that answers aren't selectable after the question has been answered
        return;
      }
      if (this.answerIds.includes(answerId)) {
        this.$emit('set-answer-ids', this.answerIds.filter(id => id !== answerId));
      } else {
        this.$emit('set-answer-ids', [...this.answerIds, answerId]);
      }
      if (this.strikes.includes(answerId)) {
        this.strikes = this.strikes.filter(id => id !== answerId);
      }
    },
    strike(value, e) {
      e.preventDefault();
      if(!this.answered) {
        if(!this.strikes.includes(value)) {
          this.strikes.push(value);
        } else {
          let index = this.strikes.findIndex(a => a == value);
          this.strikes.splice(index, 1);
        }
        if(this.selected.includes(value)) {
          this.selected = this.selected.filter(id => id != value);
        }
      }
    },
  },
};
</script>

<style lang='scss' scoped>
  .multiple-choice-component-container {
    display: flex;
    flex-direction: column;

    input {
      display: none;

      + label {
        border-bottom: 1px solid rgba(0,0,0,.12);
        display: flex;
        padding: rem-calc(16) rem-calc(12);

        @include breakpoint(small) {
          margin-right: rem-calc(-16);
          margin-left: rem-calc(-16);
        }

        @include focus-not-visible {
          outline: none;
        }

        &:hover:not(.answered),
        &.selected {
          background-color: transparentize(#00dbfc, .9);
        }

        &:not(.answered) {
          cursor: pointer;
        }

        &.answered:last-child {
          border: 0;
        }

        &:first-of-type {
          border-top: 1px solid rgba(0,0,0,.12);
        }

        .fill {
          align-items: center;
          background: $white-1;
          background-size: contain !important;
          border: 1px solid #00dbfc;
          border-radius: 100%;
          box-sizing: border-box;
          display: flex;
          height: 20px;
          justify-content: center;
          margin: rem-calc(4) 0;
          overflow: hidden;
          transition: width 0.4s ease 1.5s;
          width: 20px;
          // important! this weird css rule prevents a css overflow animation bug
          // https://stackoverflow.com/questions/14383632/webkit-border-radius-and-overflow-bug-when-using-any-animation-transition/16681137
          -webkit-mask-image: -webkit-radial-gradient(white, black);

          span {
            color: rgba(0, 0, 0, 0);
            font-size: 10px;
            font-weight: $semi-bold;
            height: 100%;
            line-height: 18px;
            opacity: 0;
            text-align: center;
            transition: opacity 0.2s ease-in 1.3s, color 0.2s ease-in 1.8s;
            visibility: hidden;
            width: 100%;
          }
        }

        &:hover:not(.answered) {
          background-color: transparentize(#00dbfc, .9);
          border-bottom: 1px solid rgba(0,0,0,.12);
        }

        &.strike {
          .fill {
            background: #E6EAEF;
            border: 1px solid transparentize($grey-3, .7);
          }
        }

        &.selected:not(.answered) {
          .fill {
            background: $white-1;
            border: 1px solid #00dbfc;
            position: relative;

            &:after {
              background-color: #00dbfc;
              border-radius: 50%;
              content: "";
              display: block;
              font-size: 1px;
              height: 14px;
              left: 2px;
              line-height: 1px;
              position: absolute;
              top: 2px;
              width: 14px;
            };
          }

          .text {
            font-weight: $bold;
          }
        }

        &.answered {
          &.selected:not(.correct) .fill {
            @include incorrect-svg(#dc3416);
            background-color: white;
            border: none;

            &.stats {
              span {
                background: #dc3416;
                color: white;
              }
            }
          }

          &.correct .fill {
            @include correct-svg(#00bc5c);
            background-color: white;
            border: none;

            &.stats {
              span {
                background: #00bc5c;
                color: white;
              }
            }
          }

          .fill.stats {
            border-radius: 10px;
            width: 38px;

            @include breakpoint(small) {
              width: 32px;
            }

            span {
              color: black;
              opacity: 1;
              visibility: visible;
            }
          }
        }

        &.correct .answer-text {
          font-weight: $bold;
        }

        .text {
          color: #000000;
          flex: 1;
          line-height: rem-calc(24);
          margin: 0 rem-calc(16);

          .answer-text {
            /deep/ p {
              margin: 0;
            }

            /deep/ img {
              box-sizing: border-box;
              max-height: rem-calc(560);
            }

            // Used to account for adjusted text size when bold
            &::after {
              display: block;
              content: attr(content);
              font-weight: $bold;
              height: 0;
              overflow: hidden;
              visibility: hidden;
            }
          }

          .rationale-text {
            color: #767676;
            font-size: rem-calc(14);
            line-height: rem-calc(21);
            padding: rem-calc(8) 0;
            &.locked {
              filter: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg"><filter id="filter"><feGaussianBlur stdDeviation="5" /></filter></svg>#filter');
              filter: blur(4px);
            }
            /deep/ p {
              margin: 0;
            }
          }
        }

        .strike-button {
          padding: 0;
          background-color: #e5e5e5;
          border-radius: 100%;
          height: rem-calc(18);
          margin: rem-calc(4) 0 rem-calc(4) auto;
          width: rem-calc(18);

          img {
            margin-bottom: rem-calc(2);
            width: rem-calc(8);
          }

          .white-icon {
            display: none;
          }

          &:hover {
            background-color: #000000;

            .white-icon {
              display: initial;
            }

            .colored-icon {
              display: none;
            }
          }
        }

        &.strike {
          .strike-button {
            background-color: #dc3416;
            .white-icon {
              display: initial;
            }

            .colored-icon {
              display: none;
            }
          }

          .answer-text {
            text-decoration: line-through;
          }
        }

        &.box {
          .fill {
            background: $white-1;
            background-size: contain !important;
            border: 1px solid #00dbfc;
            border-radius: rem-calc(4) !important;
            box-sizing: border-box;
            height: 20px;
            margin: rem-calc(4) 0;
            width: 20px;
          }

          &.selected:not(.answered) {
            .fill:after {
              border-radius: 2px;
            }
          }

          &.answered {
            &.selected:not(.correct) .fill {
              box-shadow: inset 0 0 0 4px #dc3416;
            }

            &.correct .fill {
              box-shadow: inset 0 0 0 4px #00bc5c;
            }
          }

          &.strike {
            .fill {
              background: #E6EAEF;
              border: 1px solid transparentize($grey-3, .7);
            }
          }
        }
      }
    }
  }
</style>
